"use client";

import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import {
  ResponsiveModal,
  AdvancedCalendar,
  CalendarContextProvider,
  useGlobalModalStack,
} from "@wtc/ui";
import { Select, SelectItem } from "@/components/ui/simple-select";
import { DetailActionProvider } from "@/contexts/DetailActionContext";
import { useOpenModal } from "@/lib/modals";
import { getModalConfig, isModalConfigured } from "@/lib/modals/url/modal-url-config";
import {
  getDateFields,
  getTextFields,
  transformEntitiesToCalendarEvents,
  getColorLegend,
  getDisplayNameForValue,
} from "../utils/calendar-utils";
import type { SimpleTableConfig, BaseEntity, EntityHooks } from "../types";
import type { CalendarEvent } from "@wtc/ui/types/calendar";
import type { DetailActionContext } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { DetailPageConfig } from "@/components/shared/UniversalDetailPage/types/config-types";

interface TableCalendarModalProps<TData extends BaseEntity> {
  data: TData[];
  config: SimpleTableConfig<TData>;
}

interface LegendItem {
  value: string;
  displayName: string;
  color: string;
  count: number;
}

export function TableCalendarModal<TData extends BaseEntity>({
  data,
  config,
}: TableCalendarModalProps<TData>) {
  const [selectedDateField, setSelectedDateField] = useState<string>("");
  const [selectedColorField, setSelectedColorField] = useState<string>("");
  const [legendWithNames, setLegendWithNames] = useState<LegendItem[]>([]);
  const router = useRouter();
  const openHubModal = useOpenModal();

  // Get global modal stack
  const { closeModal, modalStack, openModal } = useGlobalModalStack();
  const modalId = `calendar-view-${config.entity}`;
  const isOpen = modalStack.state[modalId] || false;

  // Get available date and text fields
  const dateFields = useMemo(() => getDateFields(config), [config]);
  const textFields = useMemo(() => getTextFields(config), [config]);

  // Default to first date field on mount
  useEffect(() => {
    if (!selectedDateField && dateFields.length > 0) {
      setSelectedDateField(dateFields[0].id);
    }
  }, [dateFields, selectedDateField]);

  // Transform data to calendar events with optional color coding
  const calendarEvents = useMemo(() => {
    if (!selectedDateField) return [];
    const colorField = selectedColorField === "__none__" ? undefined : selectedColorField;
    return transformEntitiesToCalendarEvents(
      data,
      selectedDateField,
      config,
      colorField || undefined
    );
  }, [data, selectedDateField, selectedColorField, config]);

  // Get color legend for selected field
  const colorLegend = useMemo(() => {
    if (!selectedColorField || selectedColorField === "__none__") return [];
    return getColorLegend(data, selectedColorField);
  }, [data, selectedColorField]);

  // Fetch friendly names for legend items
  useEffect(() => {
    const fetchNames = async () => {
      const itemsWithNames = await Promise.all(
        colorLegend.map(async (item) => ({
          ...item,
          displayName: await getDisplayNameForValue(item.value),
        }))
      );
      setLegendWithNames(itemsWithNames);
    };

    if (colorLegend.length > 0) {
      void fetchNames();
    } else {
      setLegendWithNames([]);
    }
  }, [colorLegend]);

  // Create action context for detail modal.
  // Calendar modal does not surface mutations — provide a strictly-typed
  // EntityHooks stub whose mutation paths throw if accidentally invoked.
  const stubHooks = useMemo<EntityHooks<TData>>(() => {
    const notImplemented = (): never => {
      throw new Error("TableCalendarModal stub hooks do not support mutations");
    };
    return {
      usePaginated: () => ({ data: undefined, isLoading: false, error: null, refetch: () => {} }),
      useInfinite: () => ({
        data: undefined,
        isLoading: false,
        error: null,
        refetch: () => {},
        hasNextPage: false,
        isFetchingNextPage: false,
        fetchNextPage: () => {},
      }),
      useSingle: () => ({ data: undefined, isLoading: false, error: null, refetch: () => {} }),
      useCreate: () => ({ mutateAsync: async () => notImplemented(), isPending: false }),
      useUpdate: () => ({ mutateAsync: async () => notImplemented(), isPending: false }),
      useDelete: () => ({ mutateAsync: async () => notImplemented(), isPending: false }),
    };
  }, []);

  const actionContext = useMemo<DetailActionContext<TData>>(() => {
    // Build a minimal-valid DetailPageConfig — calendar modal only needs the
    // entityName field downstream (consumed by UniversalDetailActions).
    const stubDetailConfig: DetailPageConfig<TData> = {
      entity: config.entity,
      displayName: config.entity,
      tabs: [],
      actions: [],
    };
    return {
      pageConfig: {
        entityName: config.entity,
        config: stubDetailConfig,
        hooks: stubHooks,
      },
      item: null as any,
      entityName: config.entity,
      entityIds: {},
      onRefresh: () => {},
      onNavigate: (href: string) => router.push(href),
      onToast: () => {},
      onOpenModal: (modalIdToOpen: string, modalData?: Record<string, any>) => {
        openModal(modalIdToOpen, modalData);
      },
      onCloseModal: (modalIdToClose: string) => {
        closeModal(modalIdToClose);
      },
      onUpdateItem: async () => {},
      onDeleteItem: async () => {},
      openModalTypeSafe: (modalIdToOpen: string, modalData?: Record<string, any>) => {
        openModal(modalIdToOpen, modalData);
      },
    };
  }, [router, openModal, closeModal, stubHooks, config]);

  // Handle event click to open detail modal via global modal stack
  const handleEventClick = useCallback(
    (event: CalendarEvent) => {
      // Find the entity data from the calendar events
      const entityData = data.find((item) => item.id === event.id);

      if (!entityData) return;

      const detailModalId = `${config.entity}-detail`;
      if (!isModalConfigured(detailModalId)) {
        return;
      }
      const modalConfig = getModalConfig(detailModalId);
      const entityIdParam = modalConfig.pluralParam;

      // Also call openModal with entity data payload
      openHubModal(
        detailModalId,
        {
          [entityIdParam]: event.id,
        },
        {
          data: {
            id: event.id,
            item: entityData,
            [entityIdParam]: event.id,
          },
        }
      );
    },
    [config.entity, data, openHubModal]
  );

  // Handle double-click - prevent default navigation, just do single-click behavior
  const handleEventDoubleClick = useCallback(
    (event: CalendarEvent) => {
      // Double-click should do the same as single-click in calendar view
      // This prevents the default /unifiedtime/event/<id> navigation
      handleEventClick(event);
    },
    [handleEventClick]
  );

  return (
    <ResponsiveModal
      globalModalId={modalId}
      title={`Calendar View - ${config.displayName}`}
      maxWidth="6xl"
      className="min-h-[80vh]"
    >
      {/* Wrap calendar with action context so detail modal can access it */}
      <DetailActionProvider value={actionContext}>
        {/* Date Field Selector and Color Field Selector */}
        <div className="flex flex-col gap-4 mb-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Show by:</label>
              <Select value={selectedDateField} onValueChange={setSelectedDateField}>
                {dateFields.map((field) => (
                  <SelectItem key={field.id} value={field.id}>
                    {field.label}
                  </SelectItem>
                ))}
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Color by:</label>
              <Select value={selectedColorField} onValueChange={setSelectedColorField}>
                <SelectItem value="__none__">None</SelectItem>
                {textFields.map((field) => (
                  <SelectItem key={field.id} value={field.id}>
                    {field.label}
                  </SelectItem>
                ))}
              </Select>
            </div>

            <span className="text-xs text-gray-500 ml-auto">
              {calendarEvents.length} of {data.length} items
            </span>
          </div>

          {/* Color Legend */}
          {legendWithNames.length > 0 && (
            <div className="flex flex-wrap gap-3 p-3 bg-card-50 rounded-lg border border-gray-200 dark:border-gray-700">
              {legendWithNames.map(({ value, displayName, color, count }) => (
                <div key={value} className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded border border-gray-300 dark:border-gray-700"
                    style={{ backgroundColor: color }}
                    title={value}
                  />
                  <span className="text-xs text-gray-700 dark:text-gray-300">
                    {displayName} <span className="text-gray-500">({count})</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Calendar */}
        {calendarEvents.length > 0 ? (
          <CalendarContextProvider initialDate={new Date()} enabled={true}>
            <AdvancedCalendar
              events={calendarEvents}
              isLoading={false}
              onEventClick={handleEventClick}
              onEventDoubleClick={handleEventDoubleClick}
            />
          </CalendarContextProvider>
        ) : (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <div className="text-center">
              <p className="text-sm">No events to display</p>
              <p className="text-xs text-gray-400 mt-1">
                Try adjusting your filters or selecting a different date field
              </p>
            </div>
          </div>
        )}
      </DetailActionProvider>
    </ResponsiveModal>
  );
}
